/** The Vendors band's charts - ADR-0212, package P9.
 *
 * Both are ranked lists over the one vendor summary the table is made from (`/sources/vendors/summary`),
 * so a bar cannot disagree with its row. A vendor's tonnage is read through its meters (ADR-0062); a
 * vendor with no reading has no figure and is counted in the foot line, never drawn as a zero. A bar
 * opens the figures behind the vendor, as the table's number does. **Top vendors is drawn in its
 * scopes' colours** (the vendor summary states each vendor's tonnes by scope), because a bar of tonnes
 * takes its scope's colour; "Figures per vendor" is a count and keeps the teal ramp.
 */

import { HBarList } from "../../components/charts";
import type { BarSegment, HBarItem } from "../../components/charts";
import type { VendorSummary } from "../../api/vendorSummary";
import { EmptyState } from "../../components/ui";
import { quantity, tonnes } from "../../lib/format";
import { BandCard } from "./BandCard";
import type { BandRead } from "./BandCard";
import { geometryTonnes } from "./bandModel";

const SCOPE_COLOUR = { "1": "scope1", "2": "scope2", "3": "scope3" } as const;

/** A vendor's tonnes by scope, from the summary's own parts. A part that is not scope 1, 2 or 3 is
 * left out rather than guessed a colour. */
function segmentsOf(vendor: NonNullable<VendorSummary>["vendors"][number]): BarSegment[] {
  return (vendor.by_scope ?? []).flatMap((part) => {
    const scope = part.scope as keyof typeof SCOPE_COLOUR;
    const value = geometryTonnes(part.kgco2e);
    return scope in SCOPE_COLOUR && value > 0
      ? [{ key: scope, label: `Scope ${scope}`, colour: SCOPE_COLOUR[scope], value, text: `${tonnes(part.kgco2e)} t` }]
      : [];
  });
}

export function TopVendorsCard({
  read,
  summary,
  onOpen,
}: {
  read: BandRead;
  summary: VendorSummary | undefined;
  /** Opens the figures behind a vendor; called with `vendor-<id>`. */
  onOpen: (key: string) => void;
}) {
  const vendors = summary?.vendors ?? [];
  // No share is printed: the vendors' tonnage is not the inventory's total (not every figure goes
  // through a vendor), so there is no whole to take a share of.
  const items: HBarItem[] = [...vendors]
    .sort((a, b) => Number(b.kgco2e ?? "-1") - Number(a.kgco2e ?? "-1"))
    .map((vendor) => ({
      key: vendor.id,
      label: vendor.name,
      value: vendor.kgco2e === null ? 0 : Math.max(0, geometryTonnes(vendor.kgco2e)),
      text: vendor.kgco2e === null ? "No reading" : `${tonnes(vendor.kgco2e)} t`,
      segments: segmentsOf(vendor),
      ...(vendor.kgco2e === null ? { notAssessed: true } : { receipt: `vendor-${vendor.id}` }),
    }));
  return (
    <BandCard title="Top vendors" unit="tCO2e" reads={[read]} testId="band-top-vendors">
      {items.length > 0 ? (
        <HBarList items={items} caption="Top vendors by tCO2e" unit="tCO2e" onOpenReceipt={onOpen} testId="band-vendor-list" />
      ) : (
        <EmptyState title="No vendors recorded" compact />
      )}
    </BandCard>
  );
}

export function VendorFiguresCard({
  read,
  summary,
  onOpen,
}: {
  read: BandRead;
  summary: VendorSummary | undefined;
  onOpen: (key: string) => void;
}) {
  const vendors = summary?.vendors ?? [];
  const items: HBarItem[] = [...vendors]
    .sort((a, b) => b.figure_count - a.figure_count)
    .map((vendor) => ({
      key: vendor.id,
      label: vendor.name,
      value: vendor.figure_count,
      text: quantity(String(vendor.figure_count)),
      ...(vendor.figure_count > 0 ? { receipt: `vendor-${vendor.id}` } : { notAssessed: true }),
    }));
  return (
    <BandCard title="Figures per vendor" unit="figures" reads={[read]} testId="band-vendor-figures">
      {items.length > 0 ? (
        <HBarList items={items} caption="Figures per vendor" unit="figures" onOpenReceipt={onOpen} testId="band-vendor-figure-list" />
      ) : (
        <EmptyState title="No vendors recorded" compact />
      )}
    </BandCard>
  );
}
